import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAdminAuth } from './AdminAuthContext';
import './AdminPage.css';

interface Submission {
  id: string;
  created_at: string;
  company_name: string;
  business_email: string | null;
  privacy_consent: number;
  privacy_consent_at: string | null;
  cross_border_consent: number | null;
  cross_border_consent_at: string | null;
  responses: Record<string, any>;
}

const AdminSubmissionDetail = () => {
  const { id } = useParams();
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { logout } = useAdminAuth();

  useEffect(() => {
    const fetchSubmission = async () => {
      if (!id) return;

      try {
        setIsLoading(true);
        setError('');

        const response = await fetch(`/api/admin/submissions/${id}`, {
          method: 'GET',
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          setSubmission(data);
        } else if (response.status === 401) {
          // Session expired, clear auth state
          logout();
        } else if (response.status === 404) {
          setError('Submission not found');
        } else {
          throw new Error(`Failed to load submission: ${response.status}`);
        }
      } catch (err) {
        console.error('Error fetching submission:', err);
        setError('Failed to load submission. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubmission();
  }, [id, navigate, logout]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderResponseValue = (value: any): string => {
    if (value === null || value === undefined) return '-';
    if (Array.isArray(value)) {
      if (value.length === 0) return '-';
      // Check if it's an array of objects (repeatable table)
      if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
        return `(${value.length} row${value.length !== 1 ? 's' : ''})`;
      }
      return value.join(', ');
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };

  const getLabelForField = (fieldId: string): string => {
    const labels: Record<string, string> = {
      companyName: 'Company name',
      businessEmail: 'Business email address',
      industry: 'Industry',
      annualRevenue: 'Approximate annual revenue',
      employeeCount: 'Number of employees worldwide',
      mainProductsServices: 'Main products / services',
      mainCompetitors: 'Main competitors',
      sellsInChina: 'Sells products in China',
      sellsInChinaComment: 'Comment on selling in China',
      sourcesFromChina: 'Sources from China',
      sourcesFromChinaComment: 'Comment on sourcing from China',
      hasLegalEntity: 'Has legal entity in China',
      hasLegalEntityComment: 'Comment on legal entity',
      hasOfficeOrEmployees: 'Has office or employees in China',
      hasOfficeOrEmployeesComment: 'Comment on office/employees',
      howSellInChina: 'How do you currently sell in China?',
      chinaFunctions: 'Functions performed in China',
      chinaFunctionsOther: 'Other functions in China',
      chinaEmployeesCount: 'Number of employees in China',
      plannedActivities: 'Planned activities in China',
      chinaProjectStatus: 'China project status',
      motivationGainingLocalBusiness: 'Gaining local Chinese business',
      motivationFollowingCustomers: 'Following existing customers',
      motivationCostReduction: 'Reduction of costs',
      motivationLocalContent: 'Delivering local content',
      motivationDeliveryTimelines: 'Shortening delivery / service timelines',
      motivationLocalCurrency: 'Invoicing in local currency',
      motivationLogisticsCosts: 'Reduction of logistics costs',
      motivationOther: 'Other motivation',
      certificationsRequired: 'Certifications required',
      certificationsDetails: 'Certifications details',
      shiftWorkPlanned: 'Shift work planned',
      shiftCount: 'Number of shifts',
      physicalSpaceRequirement: 'Physical space requirement',
      requiredSpaceTypes: 'Required space types',
      requiredOfficeArea: 'Required office area [sqm]',
      requiredWorkshopArea: 'Required workshop area [sqm]',
      requiredWarehouseArea: 'Required warehouse area [sqm]',
      requiredLaboratoryArea: 'Required laboratory area [sqm]',
      specialRequirements: 'Special requirements',
      specialRequirementsDetails: 'Special requirements details',
      businessPlanningProducts: 'Business planning products',
      tradingSales: 'Trading sales',
      materialRate: 'Average material rate [%]',
      staffPlanning: 'Staff planning',
      investmentPlanning: 'Investment planning',
      drivingInterest: 'Driving interest in China',
      currentChallenges: 'Current challenges regarding China',
      supportPriorities: 'Support priorities',
      privacyConsent: 'Privacy consent'
    };

    return labels[fieldId] || fieldId.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  const renderResponses = () => {
    if (!submission || !submission.responses) return null;

    const responses = submission.responses;
    const responseEntries = Object.entries(responses);

    // Group responses by logical sections
    const sections: Record<string, Record<string, any>> = {
      'Company Profile': {},
      'China Presence': {},
      'Current Challenges': {},
      'Support Priorities': {}
    };

    // Map field IDs to sections
    const fieldToSection: Record<string, string> = {
      companyName: 'Company Profile',
      businessEmail: 'Company Profile',
      industry: 'Company Profile',
      annualRevenue: 'Company Profile',
      employeeCount: 'Company Profile',
      mainProductsServices: 'Company Profile',
      mainCompetitors: 'Company Profile',
      sellsInChina: 'Current China Activities',
      sellsInChinaComment: 'Current China Activities',
      sourcesFromChina: 'Current China Activities',
      sourcesFromChinaComment: 'Current China Activities',
      hasLegalEntity: 'Current China Activities',
      hasLegalEntityComment: 'Current China Activities',
      hasOfficeOrEmployees: 'Current China Activities',
      hasOfficeOrEmployeesComment: 'Current China Activities',
      howSellInChina: 'Current China Activities',
      chinaFunctions: 'Current China Activities',
      chinaFunctionsOther: 'Current China Activities',
      chinaEmployeesCount: 'Current China Activities',
      plannedActivities: 'China Plans',
      chinaProjectStatus: 'China Plans',
      motivationGainingLocalBusiness: 'Motivation',
      motivationFollowingCustomers: 'Motivation',
      motivationCostReduction: 'Motivation',
      motivationLocalContent: 'Motivation',
      motivationDeliveryTimelines: 'Motivation',
      motivationLocalCurrency: 'Motivation',
      motivationLogisticsCosts: 'Motivation',
      motivationOther: 'Motivation',
      certificationsRequired: 'Concrete Setup',
      certificationsDetails: 'Concrete Setup',
      shiftWorkPlanned: 'Concrete Setup',
      shiftCount: 'Concrete Setup',
      physicalSpaceRequirement: 'Facility Requirement',
      requiredSpaceTypes: 'Facility Requirement',
      requiredOfficeArea: 'Facility Requirement',
      requiredWorkshopArea: 'Facility Requirement',
      requiredWarehouseArea: 'Facility Requirement',
      requiredLaboratoryArea: 'Facility Requirement',
      specialRequirements: 'Facility Requirement',
      specialRequirementsDetails: 'Facility Requirement',
      businessPlanningProducts: 'Business Planning',
      tradingSales: 'Business Planning',
      materialRate: 'Business Planning',
      staffPlanning: 'Staff Planning',
      investmentPlanning: 'Investment Planning',
      drivingInterest: 'Challenges & Support',
      currentChallenges: 'Challenges & Support',
      supportPriorities: 'Challenges & Support'
    };

    // Distribute responses to sections
    responseEntries.forEach(([key, value]) => {
      // Skip privacyConsent as it's already shown in metadata
      if (key === 'privacyConsent') return;

      const sectionName = fieldToSection[key] || 'Other';
      if (!sections[sectionName]) {
        sections[sectionName] = {};
      }
      sections[sectionName][key] = value;
    });

    return Object.entries(sections)
      .filter(([_, fields]) => Object.keys(fields).length > 0)
      .map(([sectionName, fields]) => {
        // Special handling for Facility Requirement section to add calculated gross areas
        if (sectionName === 'Facility Requirement') {
          // Extract raw values for calculation
          const requiredOfficeArea = submission.responses.requiredOfficeArea;
          const requiredWorkshopArea = submission.responses.requiredWorkshopArea;

          // Convert to number if they exist
          const officeArea = requiredOfficeArea != null && !isNaN(parseFloat(requiredOfficeArea)) ? parseFloat(requiredOfficeArea) : null;
          const workshopArea = requiredWorkshopArea != null && !isNaN(parseFloat(requiredWorkshopArea)) ? parseFloat(requiredWorkshopArea) : null;

          // Calculate gross areas
          let officeGross = null;
          let workshopGross = null;

          if (officeArea != null && officeArea > 0) {
            const grossValue = officeArea * 2.85;
            officeGross = grossValue % 1 === 0 ? Math.round(grossValue) : parseFloat(grossValue.toFixed(1));
          }

          if (workshopArea != null && workshopArea > 0) {
            const grossValue = workshopArea * 1.45;
            workshopGross = grossValue % 1 === 0 ? Math.round(grossValue) : parseFloat(grossValue.toFixed(1));
          }

          return (
            <div key={sectionName} className="response-group">
              <h4>{sectionName}</h4>
              {Object.entries(fields).map(([key, value]) => (
                <div key={key} className="response-item">
                  <span className="response-label">{key === 'requiredOfficeArea' ? 'Required office net area' : key === 'requiredWorkshopArea' ? 'Required workshop net area' : key === 'requiredWarehouseArea' ? 'Required warehouse area' : key === 'requiredLaboratoryArea' ? 'Required laboratory area' : getLabelForField(key)}:</span>
                  <span className="response-value">{key === 'requiredOfficeArea' || key === 'requiredWorkshopArea' || key === 'requiredWarehouseArea' || key === 'requiredLaboratoryArea' ? `${renderResponseValue(value)} sqm` : renderResponseValue(value)}</span>
                </div>
              ))}
              {(officeGross !== null || workshopGross !== null) && (
                <div className="response-group" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
                  <h5>Calculated space requirement</h5>
                  {officeGross !== null && (
                    <div className="response-item">
                      <span className="response-label">Estimated gross office area:</span>
                      <span className="response-value">{officeGross} sqm</span>
                    </div>
                  )}
                  {workshopGross !== null && (
                    <div className="response-item">
                      <span className="response-label">Estimated gross workshop area:</span>
                      <span className="response-value">{workshopGross} sqm</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        }

        // Default rendering for other sections
        return (
          <div key={sectionName} className="response-group">
            <h4>{sectionName}</h4>
            {Object.entries(fields).map(([key, value]) => (
              <div key={key} className="response-item">
                <span className="response-label">{key === 'requiredOfficeArea' ? 'Required office net area' : key === 'requiredWorkshopArea' ? 'Required workshop net area' : key === 'requiredWarehouseArea' ? 'Required warehouse area' : key === 'requiredLaboratoryArea' ? 'Required laboratory area' : getLabelForField(key)}:</span>
                <span className="response-value">{key === 'requiredOfficeArea' || key === 'requiredWorkshopArea' || key === 'requiredWarehouseArea' || key === 'requiredLaboratoryArea' ? `${renderResponseValue(value)} sqm` : renderResponseValue(value)}</span>
              </div>
            ))}
          </div>
        );
      });
  };

  return (
    <div className="admin-submission-detail">
      <div className="container">
        <div className="submission-header">
          <Link to="/admin/dashboard" className="btn btn-outline back-btn">
            鈫?Back to submissions
          </Link>
          <h1>China Readiness Check</h1>
          <h2>Submission Detail</h2>
        </div>

        {error && <div className="form-error">{error}</div>}

        {isLoading ? (
          <div className="loading">Loading submission...</div>
        ) : submission ? (
          <>
            <div className="submission-metadata">
              <div className="metadata-row">
                <span className="metadata-label">Company name</span>
                <span className="metadata-value">{submission.company_name}</span>
              </div>
              <div className="metadata-row">
                <span className="metadata-label">Business email</span>
                <span className="metadata-value">{submission.business_email || '-'}</span>
              </div>
              <div className="metadata-row">
                <span className="metadata-label">Submission date</span>
                <span className="metadata-value">{formatDate(submission.created_at)}</span>
              </div>
              <div className="metadata-row">
                <span className="metadata-label">Privacy consent</span>
                <span className="metadata-value">{submission.privacy_consent ? 'Yes' : 'No'}</span>
              </div>
              {submission.privacy_consent_at && (
                <div className="metadata-row">
                  <span className="metadata-label">Consent timestamp</span>
                  <span className="metadata-value">{formatDate(submission.privacy_consent_at)}</span>
                </div>
              )}
              <div className="metadata-row">
                <span className="metadata-label">Cross-border consent</span>
                <span className="metadata-value">{submission.cross_border_consent === 1 ? 'Yes' : submission.cross_border_consent === 0 ? 'No' : 'Not recorded'}</span>
              </div>
              {submission.cross_border_consent_at && (
                <div className="metadata-row">
                  <span className="metadata-label">Cross-border consent date</span>
                  <span className="metadata-value">{formatDate(submission.cross_border_consent_at)}</span>
                </div>
              )}
            </div>

            <div className="responses-section">
              <h3>Questionnaire Responses</h3>
              {renderResponses()}
              {Object.keys(submission.responses).length === 0 && (
                <p>No questionnaire responses available.</p>
              )}
            </div>
          </>
        ) : (
          <div className="empty-state">
            <h3>Submission not found</h3>
            <p>The requested submission could not be found.</p>
          </div>
        )}

        {/* Delete submission button */}
        {submission && (
          <div className="delete-section" style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#fff8f8', border: '1px solid #ffe0e0', borderRadius: '4px' }}>
            <button
              onClick={async () => {
                if (window.confirm('Delete this submission permanently?\n\nThis action cannot be undone.')) {
                  try {
                    const response = await fetch(`/api/admin/submissions/${id}`, {
                      method: 'DELETE',
                      credentials: 'include'
                    });

                    if (response.ok) {
                      navigate('/admin/dashboard');
                    } else if (response.status === 404) {
                      setError('Submission not found');
                    } else {
                      const data = await response.json();
                      setError(data.error || 'Failed to delete submission');
                    }
                  } catch (err) {
                    console.error('Error deleting submission:', err);
                    setError('Failed to delete submission. Please try again.');
                  }
                }
              }}
              className="btn btn-danger"
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: 'bold'
              }}
            >
              Delete submission
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSubmissionDetail;
